import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";

import { consumeReturnTo, rememberReturnTo } from "../lib/auth";
import { Login } from "./Login";

/** 未認証なら Keycloak のログイン画面へリダイレクトする。 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const shouldRedirect =
    !auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !auth.error;

  useEffect(() => {
    if (shouldRedirect) {
      rememberReturnTo(location.pathname + location.search + location.hash);
      void auth.signinRedirect();
    }
  }, [shouldRedirect, auth, location.pathname, location.search, location.hash]);

  // Keycloak から戻ってきた直後(URL に code / state が残っている)だけ、
  // 元居たページへ react-router で移す。あわせて code / state も URL から消える。
  // history.replaceState で URL だけ書き換えても react-router は気づかないので、ここでやる。
  const restored = useRef(false);
  useEffect(() => {
    if (!auth.isAuthenticated || restored.current) return;
    const params = new URLSearchParams(location.search);
    if (!params.has("code") && !params.has("state")) return;
    restored.current = true;
    // 戻り先が `/` のときは `/portal` へ直接移す。`/` の index ルートの <Navigate to="/portal"> は、
    // この effect より先(子の effect が先に走る)に /portal へ移したあと、ここで `/` に戻されると
    // 同じ要素のまま再描画されて 2 度目の遷移をしない。結果、ホームが空のまま止まる(R6c-1 fix1)
    const back = consumeReturnTo();
    navigate(!back || back === "/" ? "/portal" : back, { replace: true });
  }, [auth.isAuthenticated, location.search, navigate]);

  const login = () => {
    rememberReturnTo(location.pathname + location.search + location.hash);
    void auth.signinRedirect();
  };

  if (auth.error) return <Login error={auth.error.message} onLogin={login} />;
  if (!auth.isAuthenticated) return <Login onLogin={login} />;
  return <>{children}</>;
}
